// Dados do cliente para os documentos jurídicos da UC: aditivo, advertência,
// notificação extrajudicial e distrato.
//
// Os quatro modais preenchiam o formulário só com o cadastro da UC, e o cadastro
// tem buraco. Medido em 16/10/2026 sobre as 1.565 UCs não canceladas: 874 sem
// e-mail, 414 sem WhatsApp, 314 sem logradouro e 977 sem número. A pessoa abria
// o modal, escolhia a UC e ainda tinha que ir buscar o resto em outra tela.
//
// O que completa (só o que está vazio no cadastro da UC):
// - negociação do CRM ligada à UC: +128 e-mails, +198 WhatsApps e o
//   representante legal de 106 das 412 empresas;
// - outras negociações e UCs do mesmo CPF/CNPJ: mais ~180 e-mails;
// - última fatura da Equatorial: +113 logradouros, +119 CEPs, +132 bairros.
// O pagador da UC (tabela `pagadores`) foi medido e não acrescenta nada.
//
// Sem imports de runtime além de .ts relativos: roda sob `node --test`.

import { limpaEnderecoFatura } from '../../supabase/functions/_shared/endereco-ruido.ts';
import { derivarStatusBoleto, valorDevido, type DadosStatusBoleto } from './statusBoleto.ts';
import { normalizeUcCodigo } from './ucCodigo.ts';

/** O que se lê da UC. Estrutural: o `select` do UCSearchPicker e o `*` da lista servem. */
export interface UcParaDocumento {
    nome_cliente?: string | null;
    titular_fatura?: string | null;
    cpf_cnpj_cliente?: string | null;
    cpf_cnpj_titular?: string | null;
    email_cliente?: string | null;
    outro_email?: string | null;
    whatsapp_cliente?: string | null;
    whatsapp_cliente_2?: string | null;
    cep?: string | null;
    logradouro?: string | null;
    numero?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    /** Campo legado: o endereço inteiro num texto só. */
    endereco?: string | null;
}

export interface NegociacaoParaDocumento {
    cliente_nome?: string | null;
    cpf_cnpj?: string | null;
    tipo_cliente?: string | null;
    email?: string | null;
    whatsapp?: string | null;
    email_financeiro?: string | null;
    whatsapp_financeiro?: string | null;
    nome_representante_legal?: string | null;
    cpf_representante?: string | null;
}

/** Endereço impresso na fatura da Equatorial (colunas `end_*` de `faturas`). */
export interface FaturaEnderecoDoc {
    end_logradouro?: string | null;
    end_numero?: string | null;
    end_bairro?: string | null;
    end_cep?: string | null;
    end_cidade?: string | null;
    end_uf?: string | null;
}

export interface EnderecoDocumento {
    cep: string;
    logradouro: string;
    numero: string;
    bairro: string;
    cidade: string;
    estado: string;
}

export interface DadosDocumentoUc {
    nome: string;
    cpfCnpj: string;
    tipoCliente: 'PF' | 'PJ';
    email: string;
    whatsapp: string;
    /** Representante legal da empresa notificada. Vazio para pessoa física. */
    representanteNome: string;
    representanteCpf: string;
    endereco: EnderecoDocumento;
    /** "Rua, número, bairro, cidade/UF" — o texto que vai para o documento. */
    enderecoTexto: string;
}

const limpo = (v: string | null | undefined): string => String(v ?? '').trim();
const digitos = (v: string | null | undefined): string => String(v ?? '').replace(/\D/g, '');
const primeiro = (...vs: (string | null | undefined)[]): string => {
    for (const v of vs) {
        const t = limpo(v);
        if (t) return t;
    }
    return '';
};

/**
 * O campo legado `endereco`, ou '' quando ele não diz nada.
 *
 * 126 UCs têm ali só a máscara vazia de quem montou o texto: ",  - , /". Isso ia
 * parar no documento como endereço.
 */
export const enderecoLegadoUtil = (endereco: string | null | undefined): string => {
    const t = limpo(endereco);
    return /[\p{L}\d]/u.test(t) ? t : '';
};

/** Nome da rua sem caixa, acento e bagagem — para saber se a fatura fala da mesma rua. */
const chaveRua = (logradouro: string): string =>
    logradouro.split(',')[0]
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toUpperCase().replace(/[^A-Z0-9]/g, '');

/** "Rua, número, bairro, cidade/UF". Mesmo formato do `composeEnderecoUC` de sempre. */
export const enderecoDocumentoTexto = (e: EnderecoDocumento): string => {
    const cidadeUf = e.cidade ? `${e.cidade}${e.estado ? `/${e.estado}` : ''}` : '';
    return [e.logradouro, e.numero, e.bairro, cidadeUf].filter(Boolean).join(', ');
};

/**
 * Endereço da UC, completado pela fatura da Equatorial.
 *
 * Com logradouro no cadastro, a fatura só completa CEP, número, bairro, cidade e
 * UF se falar da MESMA rua — senão sairia rua de um lugar com CEP de outro.
 * Sem logradouro no cadastro, não há o que contradizer: vale o da fatura.
 * Sem nenhum dos dois, o texto legado vai inteiro no logradouro.
 */
export const enderecoParaDocumento = (
    uc: UcParaDocumento,
    fatura?: FaturaEnderecoDoc | null,
): EnderecoDocumento => {
    const ruaUc = limpo(uc.logradouro);
    const ruaFatura = limpaEnderecoFatura(fatura?.end_logradouro);
    const fat = fatura && ruaFatura && (!ruaUc || chaveRua(ruaUc) === chaveRua(ruaFatura)) ? fatura : null;

    const cepFatura = digitos(fat?.end_cep).length === 8 ? limpo(fat?.end_cep) : '';
    return {
        cep: primeiro(uc.cep, cepFatura),
        logradouro: ruaUc || (fat ? ruaFatura : '') || enderecoLegadoUtil(uc.endereco),
        numero: primeiro(uc.numero, fat?.end_numero),
        bairro: primeiro(uc.bairro, fat?.end_bairro),
        cidade: primeiro(uc.cidade, fat?.end_cidade),
        estado: primeiro(uc.estado, fat?.end_uf),
    };
};

/**
 * A negociação é da mesma pessoa que a UC?
 *
 * Em 88 UCs o CPF/CNPJ da negociação ligada é outro (troca de titularidade, por
 * exemplo). Nesses casos o contato da negociação é de outra pessoa e não entra.
 */
const negociacaoDaMesmaPessoa = (docUc: string, neg: NegociacaoParaDocumento): boolean => {
    const a = digitos(docUc);
    const b = digitos(neg.cpf_cnpj);
    return !a || !b || a === b;
};

/**
 * Junta UC, negociações e fatura nos campos dos documentos.
 *
 * Nome e CPF/CNPJ saem da MESMA pessoa: o cliente contratante da UC. O titular
 * da fatura da Equatorial é só fallback. Em 211 UCs os dois documentos diferem,
 * e três dos quatro modais pegavam o do titular com o nome do cliente.
 *
 * `negs` vem em ordem de preferência: a ligada à UC primeiro, depois as outras
 * do mesmo CPF/CNPJ. `outrasUcs` são as outras UCs do mesmo cliente.
 */
export const montarDadosDocumentoUc = (
    uc: UcParaDocumento,
    negs?: NegociacaoParaDocumento | (NegociacaoParaDocumento | null)[] | null,
    fatura?: FaturaEnderecoDoc | null,
    outrasUcs: UcParaDocumento[] = [],
): DadosDocumentoUc => {
    const docUc = primeiro(uc.cpf_cnpj_cliente, uc.cpf_cnpj_titular);
    const ns = (Array.isArray(negs) ? negs : [negs])
        .filter((n): n is NegociacaoParaDocumento => !!n && negociacaoDaMesmaPessoa(docUc, n));
    const n = ns[0];

    const cpfCnpj = docUc || limpo(n?.cpf_cnpj);
    const nDig = digitos(cpfCnpj).length;
    const tipoNeg = limpo(n?.tipo_cliente).toUpperCase();
    const tipoCliente: 'PF' | 'PJ' = nDig === 14 ? 'PJ' : nDig === 11 ? 'PF' : (tipoNeg === 'PJ' ? 'PJ' : 'PF');

    // Nome e CPF do representante saem da MESMA negociação.
    const comRep = tipoCliente === 'PJ' ? ns.find(x => limpo(x.nome_representante_legal)) : undefined;

    const endereco = enderecoParaDocumento(uc, fatura);
    return {
        nome: primeiro(uc.nome_cliente, n?.cliente_nome, uc.titular_fatura),
        cpfCnpj,
        tipoCliente,
        // Contato do financeiro por último: é da empresa, mas não é o do cliente.
        email: primeiro(
            uc.email_cliente, uc.outro_email,
            ...ns.map(x => x.email), ...outrasUcs.map(u => u.email_cliente),
            ...ns.map(x => x.email_financeiro),
        ),
        whatsapp: primeiro(
            uc.whatsapp_cliente, uc.whatsapp_cliente_2,
            ...ns.map(x => x.whatsapp), ...outrasUcs.map(u => u.whatsapp_cliente),
            ...ns.map(x => x.whatsapp_financeiro),
        ),
        representanteNome: limpo(comRep?.nome_representante_legal),
        representanteCpf: limpo(comRep?.cpf_representante),
        endereco,
        enderecoTexto: enderecoDocumentoTexto(endereco),
    };
};

/** As duas grafias do CPF/CNPJ que existem no banco: só dígitos e com máscara. */
export const grafiasDocumento = (doc: string | null | undefined): string[] => {
    const d = digitos(doc);
    if (d.length === 11) return [d, d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')];
    if (d.length === 14) return [d, d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')];
    return [];
};

/**
 * Setter que só preenche campo vazio: `setEmail(seVazio(dados.email))`.
 *
 * O complemento chega depois do preenchimento pela UC (é uma busca no banco).
 * Até lá a pessoa pode ter digitado, ou a regeração ("Corrigir e gerar
 * novamente") já ter posto o valor da emissão anterior — os dois valem mais.
 *
 * `automatico` é o que o preenchimento pela UC pôs no campo. Campo que ainda
 * está com ele não foi tocado, e pode ser trocado pela versão mais completa:
 * é o caso do endereço com rua mas sem bairro, que a fatura completa.
 */
export const seVazio = (novo: string, automatico?: string) => (atual: string): string =>
    !limpo(atual) || (automatico !== undefined && atual === automatico && limpo(novo)) ? novo : atual;

// ---------------------------------------------------------------- débito da UC

/** Colunas de `faturas` que `faturasEmAberto` precisa. */
export const CAMPOS_FATURA_DEBITO = 'id, uc_codigo, mes_referencia, vencimento_boleto, data_vencimento, valor_boleto_vigor, valor_emissao_boleto_unico, status_boleto_vigor, status_fatura, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh, cobranca_agrupada';

export type FaturaDebito = DadosStatusBoleto & {
    id: number;
    uc_codigo?: string | null;
    mes_referencia?: string | null;
};

export interface FaturaEmAberto {
    id: number;
    uc_codigo: string;
    mes_referencia: string;
    vencimento_boleto: string | null;
    /** O que a Vigor cobra: no boleto único é `valor_emissao_boleto_unico`. */
    valor: number;
    status: 'Vencido' | 'Em aberto';
}

/**
 * Os números pelos quais as faturas da UC podem estar gravadas.
 *
 * A fatura vem ora com o número da UC, ora com o outro número que a Equatorial
 * dá à mesma unidade, e às vezes com ponto e traço. Buscando pelo número cru,
 * a notificação extrajudicial não achava a dívida de 125 das 469 UCs com fatura
 * em aberto (208 faturas, medido em 16/10/2026) e abria com o débito vazio.
 * O nº da UC pode ter dois códigos separados por "/" (UC antiga + nova).
 */
export const chavesFaturasUc = (
    numeroUc: string | null | undefined,
    outroNumeroUc?: string | null,
): string[] => Array.from(new Set(
    [...String(numeroUc ?? '').split('/'), outroNumeroUc]
        .map(normalizeUcCodigo)
        .filter(Boolean),
));

/**
 * As faturas que ainda são dívida, pela regra única de src/lib/statusBoleto.ts —
 * a mesma da Central de Inadimplência. Antes a notificação lia o campo cru e o
 * `valor_boleto_vigor`: perdia a fatura vencida que o campo ainda chamava de
 * "Em aberto" e a de boleto único, cujo valor fica em outra coluna.
 */
export const faturasEmAberto = (faturas: FaturaDebito[], hoje: Date = new Date()): FaturaEmAberto[] =>
    faturas
        .flatMap((f): FaturaEmAberto[] => {
            if (f.status_fatura === 'Excluído') return [];
            const status = derivarStatusBoleto(f, hoje);
            if (status !== 'Vencido' && status !== 'Em aberto') return [];
            return [{
                id: f.id,
                uc_codigo: f.uc_codigo ?? '',
                mes_referencia: f.mes_referencia ?? '',
                vencimento_boleto: f.vencimento_boleto || f.data_vencimento || null,
                valor: valorDevido(f),
                status,
            }];
        })
        .sort((a, b) => String(a.vencimento_boleto ?? '').localeCompare(String(b.vencimento_boleto ?? '')));

/** Soma das faturas vencidas — o "Faturas em atraso" dos documentos. */
export const somaVencidas = (faturas: FaturaEmAberto[]): number =>
    Math.round(faturas.reduce((s, f) => s + (f.status === 'Vencido' ? f.valor : 0), 0) * 100) / 100;
